import React, { useCallback, useEffect, useLayoutEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { Smile } from 'lucide-react';
import EmojiPicker from 'emoji-picker-react';

/**
 * Reusable text field (input or textarea) with an emoji picker, and an
 * optional action button next to the label — e.g. "Get Hindi" to
 * translate a sibling field.
 *
 * The emoji picker is rendered in a portal (position: fixed) so it never
 * gets clipped by a scrolling parent such as a modal, and it opens
 * upwards or downwards depending on the available space.
 *
 * Usage:
 *   <EmojiTextField
 *     label="Question (English)"
 *     required
 *     value={questionEn}
 *     onChange={setQuestionEn}
 *     error={errors.questionEn}
 *   />
 */

const PICKER_WIDTH = 300;
const PICKER_HEIGHT = 350;
const GAP = 8;
const EDGE = 8;

const EmojiTextField = ({
    label,
    required = false,
    value,
    onChange,
    placeholder,
    error,
    disabled = false,
    multiline = true,
    rows = 3,
    actionLabel,
    onAction,
    isActionLoading = false,
}) => {
    const [showEmoji, setShowEmoji] = useState(false);
    const [pos, setPos] = useState({ top: 0, left: 0, height: PICKER_HEIGHT });
    const fieldRef = useRef(null);
    const pickerRef = useRef(null);
    const buttonRef = useRef(null);

    // Calculate where the picker should appear (below or above the smile button)
    const updatePosition = useCallback(() => {
        const btn = buttonRef.current;
        if (!btn) return;

        const rect = btn.getBoundingClientRect();
        const vw = window.innerWidth;
        const vh = window.innerHeight;

        const height = Math.min(PICKER_HEIGHT, vh - EDGE * 2);
        const spaceBelow = vh - rect.bottom - GAP - EDGE;
        const spaceAbove = rect.top - GAP - EDGE;

        const openBelow = spaceBelow >= height || spaceBelow >= spaceAbove;

        let top = openBelow ? rect.bottom + GAP : rect.top - GAP - height;
        top = Math.max(EDGE, Math.min(top, vh - height - EDGE));

        let left = rect.right - PICKER_WIDTH;
        left = Math.max(EDGE, Math.min(left, vw - PICKER_WIDTH - EDGE));

        setPos({ top, left, height });
    }, []);

    // Position before paint so it doesn't flicker
    useLayoutEffect(() => {
        if (showEmoji) updatePosition();
    }, [showEmoji, updatePosition]);

    // Keep it attached to the button while scrolling/resizing
    useEffect(() => {
        if (!showEmoji) return undefined;
        window.addEventListener('resize', updatePosition);
        window.addEventListener('scroll', updatePosition, true); // capture: catches modal scroll too
        return () => {
            window.removeEventListener('resize', updatePosition);
            window.removeEventListener('scroll', updatePosition, true);
        };
    }, [showEmoji, updatePosition]);

    // Close on outside click (ignore the toggle button itself)
    useEffect(() => {
        if (!showEmoji) return undefined;
        const handler = (e) => {
            if (pickerRef.current?.contains(e.target)) return;
            if (buttonRef.current?.contains(e.target)) return;
            setShowEmoji(false);
        };
        document.addEventListener('mousedown', handler);
        return () => document.removeEventListener('mousedown', handler);
    }, [showEmoji]);

    // Close on Escape
    useEffect(() => {
        if (!showEmoji) return undefined;
        const handler = (e) => {
            if (e.key === 'Escape') {
                e.stopPropagation();
                setShowEmoji(false);
            }
        };
        document.addEventListener('keydown', handler);
        return () => document.removeEventListener('keydown', handler);
    }, [showEmoji]);

    // Close if the field becomes disabled (e.g. while translating)
    useEffect(() => {
        if (disabled) setShowEmoji(false);
    }, [disabled]);

    const insertEmoji = (emojiData) => {
        const emoji = emojiData.emoji;
        const el = fieldRef.current;
        const current = value || '';

        if (!el) {
            onChange(current + emoji);
            setShowEmoji(false);
            return;
        }

        const start = el.selectionStart ?? current.length;
        const end = el.selectionEnd ?? current.length;
        const next = current.slice(0, start) + emoji + current.slice(end);
        onChange(next);
        setShowEmoji(false);

        requestAnimationFrame(() => {
            el.focus();
            const caret = start + emoji.length;
            el.setSelectionRange(caret, caret);
        });
    };

    const Field = multiline ? 'textarea' : 'input';

    return (
        <div className="mb-4 relative">
            <div className="flex items-center gap-2 flex-wrap mb-1">
                <label className="block font-medium">
                    {label} {required && <span className="text-red-500 text-lg">*</span>}
                </label>
                {actionLabel && onAction && (
                    <button
                        type="button"
                        onClick={onAction}
                        disabled={isActionLoading || disabled}
                        className="ml-auto inline-flex items-center gap-1.5 px-3 py-1 rounded-md text-xs font-semibold bg-orange-500 hover:bg-orange-600 active:bg-orange-700 text-white disabled:opacity-60 transition-colors"
                    >
                        {isActionLoading ? (
                            <>
                                <span className="w-3 h-3 border-2 border-white border-t-transparent rounded-full animate-spin" />
                                Translating...
                            </>
                        ) : actionLabel}
                    </button>
                )}
            </div>

            <div className={`relative border rounded-lg ${error ? 'border-red-500' : 'border-gray-300 dark:border-gray-600'}`}>
                <Field
                    ref={fieldRef}
                    value={value}
                    onChange={(e) => onChange(e.target.value)}
                    placeholder={placeholder || `Enter ${label?.toLowerCase()}`}
                    disabled={disabled}
                    rows={multiline ? rows : undefined}
                    className={`w-full px-3 py-2 text-sm bg-transparent dark:text-gray-200 ${multiline ? 'resize-none' : ''}`}
                    style={{ border: 'none', outline: 'none', boxShadow: 'none' }}
                />
                <div className="px-2 pb-2 flex justify-end">
                    <button
                        ref={buttonRef}
                        type="button"
                        onClick={() => setShowEmoji((v) => !v)}
                        disabled={disabled}
                        className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-300"
                    >
                        <Smile size={18} />
                    </button>
                </div>
            </div>

            {showEmoji &&
                createPortal(
                    <div
                        ref={pickerRef}
                        className="shadow-xl rounded-lg overflow-hidden"
                        style={{
                            position: 'fixed',
                            top: pos.top,
                            left: pos.left,
                            zIndex: 100000, // above the modal (z-99999) and toast
                        }}
                    >
                        <EmojiPicker
                            onEmojiClick={insertEmoji}
                            width={PICKER_WIDTH}
                            height={pos.height}
                            previewConfig={{ showPreview: false }}
                            emojiStyle="native"
                            emojiVersion="12.0"
                        />
                    </div>,
                    document.body
                )}

            {error && <p className="text-red-500 text-sm mt-1">{error}</p>}
        </div>
    );
};

export default EmojiTextField;